import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Subject, takeUntil } from 'rxjs';
import { VTabComponent, Breadcrumb } from '../vtab/vtab.component';
import { VTabActionBarComponent } from '../vtab-action-bar/vtab-action-bar.component';
import { VTabRightPanelComponent } from '../vtab-right-panel/vtab-right-panel.component';
import { SettingButtonConfig } from '@sbd-doug-frontend/shared/lib/ui/setting-button/toggle-button-item.type';

@Component({
  selector: 'vtab-group',
  templateUrl: './vtab-group.component.html',
  styleUrls: ['./vtab-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VTabGroupComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() appTitle: string | null = null;
  @Input() appTitleLink: string | any[] | null = null;
  @Input() showBackButton = false;
  @Output() backButtonClicked = new EventEmitter();
  @Output() tabChange = new EventEmitter<VTabComponent>();
  @Input() rightPanelOpened = false;
  @Input() rightPanelMode: 'over' | 'side' = 'over';
  @Output() rightPanelClosed = new EventEmitter();
  @Input() showSettingButton: boolean = false;
  @Input() settingButtonConfig!: SettingButtonConfig;
  @Input() includePadding = true;

  @ContentChildren(VTabComponent) _vtabs = {} as QueryList<VTabComponent>;
  @ViewChild('drawer') drawer: MatDrawer | undefined;
  @ContentChild(VTabActionBarComponent) actionBarComponent: VTabActionBarComponent | undefined;
  @ViewChild('rightPanel') rightPanel: MatDrawer | undefined;
  @ContentChild(VTabRightPanelComponent) rightPanelComponent: VTabRightPanelComponent | undefined;
  @ViewChild('topAnchor', { read: ElementRef }) topAnchor: ElementRef | undefined;

  showActionBar = false;
  showRightPanel = false;
  drawerMode: 'over' | 'side' = 'side';
  drawerOpened = true;

  selectedTab: VTabComponent | null = null;
  firstLoad = true;
  showPanel = false;

  private _unsubscribeAll: Subject<unknown> = new Subject<unknown>();
  @Input() hideActionBarOnVtabIds: string[] = [];

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
  ) {
  }

  ngOnInit(): void {
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {

        // Set the drawerMode and drawerOpened
        if (matchingAliases.includes('lg')) {
          this.drawerMode = 'side';
          this.drawerOpened = true;
        } else {
          this.drawerMode = 'over';
          this.drawerOpened = false;
        }

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('rightPanelOpen' in changes) {
      this.rightPanelOpened = changes['rightPanelOpened'].currentValue;
    }
  }

  /**
   * After view is initialized
   */
  ngAfterViewInit(): void {
    const defaultTab = this._vtabs.find(vtab => vtab.active === true) ?? null;

    if (defaultTab !== null) {
      this.goToTab(defaultTab);
      this.firstLoad = false;
    }

    /*if (this.actionBarComponent) {
      setTimeout(() => {
        if (this.actionBarComponent && ) {
          this.showActionBar = true;
        }
      });
    }*/

    if (this.rightPanelComponent) {
      this.showRightPanel = true;
    }

    this._changeDetectorRef.detectChanges();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  /**
   * Go to a specific Tab component.
   *
   * @param tab
   */
  goToTab(tab: VTabComponent, emitTabChanged = true) {
    this._vtabs.forEach(_tab => _tab.active = false);
    tab.active = true;
    this.selectedTab = tab;

    if(this.selectedTab?.id && this.hideActionBarOnVtabIds.includes(this.selectedTab.id)){
      this.showActionBar = false;
    }else{
      this.showActionBar = true;
    }
    this._changeDetectorRef.detectChanges();

    if (!this.firstLoad && emitTabChanged) {
      this.tabChange.emit(tab);
      if (this.drawerMode === 'over') {
        this.drawer?.close();
      }
    }

  }

  goToTabById(id: string, emitTabChanged = true) {
    this._vtabs.forEach(_tab => _tab.active = false);
    const lookup = this._vtabs.find(_tab => _tab.id === id);

    if (lookup === undefined) {
      return;
    }

    lookup.active = true;
    this.selectedTab = lookup;


    if (!this.firstLoad && emitTabChanged) {
      this.tabChange.emit(this.selectedTab);
    }

    this._changeDetectorRef.detectChanges();
  }

  /**
   * Get information about a tab.
   *
   * @param tab
   * @returns
   */
  getTabInfo(tab: VTabComponent): VTabComponent {
    return tab;
  }

  /**
   * Emit the backClicked event to parent.
   */
  triggerBackClicked($event: MouseEvent = new MouseEvent('click')) {
    this.backButtonClicked.emit($event);
  }

  closeRightPanel() {
    this.rightPanel?.close();
    this.rightPanelClosed.emit();
  }

  // Allow Escape to close the left drawer only when in 'over' mode
  @HostListener('document:keydown', ['$event'])
  handleEscClose(event: KeyboardEvent) {
    if (event?.key === 'Escape' && this.drawerMode === 'over' && this.drawer?.opened) {
      this.drawer.close();
    }
  }

  scrollTop() {
    if (this.topAnchor) {
      this.topAnchor.nativeElement.scrollTop = 0;
    }
  }


  /**
   * Get breadcrumb items for the selected tab
   * @param tab
   * @returns
   */
  getBreadcrumbs(tab: VTabComponent): Breadcrumb[] {
    return tab.pageItemTitles || [];
  }

  /**
   * Check if tab has breadcrumbs
   * @param tab
   * @returns
   */
  hasBreadcrumbs(tab: VTabComponent): boolean {
    return tab.pageItemTitles && tab.pageItemTitles.length > 0;
  }
}
